import { useAssistantTool } from "@assistant-ui/react";
import { z } from "zod";
import { useReportsToolExecutor } from "@/domains/ai/ai-chat/hooks/use-reports-tool-executor";

/**
 * Register reports tools with @assistant-ui/react
 *
 * These tools allow the AI agent to change date ranges and comparison periods for reports.
 * The tools are auto-extracted by useRegisteredTools() and sent to the backend.
 */
export function useReportsTools() {
  const { executeTool } = useReportsToolExecutor();

  // Register setReportsDateRange tool
  useAssistantTool({
    toolName: "setReportsDateRange",
    description:
      "Change the date range and comparison period for reports. Use this when the user wants to view reports for a specific time period. Supports presets (e.g., 'last 30 days', 'last month') or custom date ranges. Can also set comparison strategy for year-over-year or sequential comparisons.",
    parameters: z.object({
      preset: z
        .enum([
          "last 7 days",
          "last 30 days",
          "last 90 days",
          "last 180 days",
          "last 365 days",
          "last week",
          "last month",
          "last quarter",
          "last year",
        ])
        .optional()
        .describe(
          "Preset time range. Use this for common periods like 'last 30 days' or 'last month'. Mutually exclusive with startDate/endDate."
        ),
      startDate: z
        .string()
        .optional()
        .describe(
          "Custom start date in ISO format (YYYY-MM-DD). Must be used with endDate. Mutually exclusive with preset."
        ),
      endDate: z
        .string()
        .optional()
        .describe(
          "Custom end date in ISO format (YYYY-MM-DD). Must be used with startDate. Mutually exclusive with preset."
        ),
      comparisonStrategy: z
        .enum(["previous period", "same period last year", "year over year", "custom"])
        .optional()
        .describe(
          "How to calculate the comparison period. 'previous period' = sequential (e.g., previous 30 days), 'same period last year' = year-over-year for seasonality comparison. Default: 'previous period'."
        ),
      comparisonStartDate: z
        .string()
        .optional()
        .describe(
          "Custom comparison start date in ISO format (YYYY-MM-DD). Only used when comparisonStrategy is 'custom'."
        ),
      comparisonEndDate: z
        .string()
        .optional()
        .describe(
          "Custom comparison end date in ISO format (YYYY-MM-DD). Only used when comparisonStrategy is 'custom'."
        ),
    }),
    execute: async (args) => {
      const result = executeTool("setReportsDateRange", args);
      return result;
    },
  });
}
